import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, ExternalLink, Maximize2, ZoomIn, ZoomOut } from 'lucide-react';
import { cn } from '../lib/utils';
import { ICON, IconButton, Skeleton } from './ui';

// pdfjs-dist 4.x ESM + worker via Vite's ?url suffix
import * as pdfjsLib from 'pdfjs-dist';
import PdfJsWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = PdfJsWorkerUrl;

interface Props {
  src: string;
  filename?: string | null;
  className?: string;
}

/** Bestand downloaden via een tijdelijke link (werkt ook voor een knop zonder href). */
export function downloadFile(src: string, filename?: string | null) {
  const a = document.createElement('a');
  a.href = src;
  a.download = filename ?? '';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

const openInTab = (src: string) => window.open(src, '_blank', 'noopener');

export function PdfViewer({ src, filename, className }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const renderTaskRef = useRef<{ cancel: () => void } | null>(null);
  const pdfRef = useRef<any>(null);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Zoom zodat de pagina precies de breedte van het venster vult.
  const fitTo = (pdf: any, pageNo: number) => {
    const box = scrollRef.current;
    if (!box || !pdf) return;
    pdf.getPage(pageNo).then((p: any) => {
      const vp = p.getViewport({ scale: 1 });
      const target = (box.clientWidth - 32) / vp.width;
      setZoom(Math.max(0.4, Math.min(3, +target.toFixed(2))));
    });
  };

  // Document laden als src wijzigt
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPage(1);
    const task = pdfjsLib.getDocument({ url: src, withCredentials: true });
    task.promise.then(
      (pdf: any) => {
        if (cancelled) {
          pdf.destroy();
          return;
        }
        pdfRef.current = pdf;
        setNumPages(pdf.numPages);
        fitTo(pdf, 1);
        setLoading(false);
      },
      (err: any) => {
        if (cancelled) return;
        setError(err?.message ?? 'Kon het document niet laden');
        setLoading(false);
      }
    );
    return () => {
      cancelled = true;
      try {
        task.destroy();
      } catch {}
      if (pdfRef.current) {
        try {
          pdfRef.current.destroy();
        } catch {}
        pdfRef.current = null;
      }
    };
  }, [src]);

  // Huidige pagina tekenen als pdf/pagina/zoom wijzigt
  useEffect(() => {
    const pdf = pdfRef.current;
    const canvas = canvasRef.current;
    if (!pdf || !canvas || loading) return;
    let cancelled = false;

    (async () => {
      try {
        if (renderTaskRef.current) {
          try {
            renderTaskRef.current.cancel();
          } catch {}
          renderTaskRef.current = null;
        }
        const pdfPage = await pdf.getPage(page);
        if (cancelled) return;
        const dpr = window.devicePixelRatio || 1;
        const viewport = pdfPage.getViewport({ scale: zoom * dpr });
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        canvas.style.width = `${viewport.width / dpr}px`;
        canvas.style.height = `${viewport.height / dpr}px`;

        const task = pdfPage.render({ canvasContext: ctx, viewport });
        renderTaskRef.current = task;
        await task.promise;
        renderTaskRef.current = null;
      } catch (err: any) {
        if (err?.name === 'RenderingCancelledException') return;
        if (!cancelled) setError(err?.message ?? 'Het document kon niet worden getekend');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [page, zoom, loading, numPages]);

  const isImage = /\.(png|jpe?g|webp|gif)(\?|$)/i.test(src);

  if (isImage) {
    return (
      <div className={cn('flex flex-col bg-slate-950', className)}>
        <div className="flex flex-1 items-center justify-center overflow-auto p-4">
          <img src={src} alt={filename ?? 'Document'} className="max-h-full max-w-full object-contain" />
        </div>
        <div className="flex items-center justify-end gap-1 border-t border-surface-line px-2 py-1">
          <IconButton icon={ExternalLink} label="Openen in nieuw tabblad" onClick={() => openInTab(src)} />
          <IconButton icon={Download} label="Downloaden" onClick={() => downloadFile(src, filename)} />
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col bg-slate-950', className)}>
      <div className="sticky top-0 z-10 flex items-center gap-0.5 border-b border-surface-line bg-surface px-1 py-1">
        <IconButton icon={ChevronLeft} label="Vorige pagina" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading} />
        <span className="t-num min-w-[44px] text-center text-sm text-slate-300">{loading ? '–' : `${page}/${numPages}`}</span>
        <IconButton
          icon={ChevronRight}
          label="Volgende pagina"
          onClick={() => setPage((p) => Math.min(numPages, p + 1))}
          disabled={page >= numPages || loading}
        />
        <span className="mx-1 hidden h-5 w-px bg-surface-line sm:block" aria-hidden />
        <IconButton icon={ZoomOut} label="Uitzoomen" onClick={() => setZoom((z) => Math.max(0.4, +(z - 0.2).toFixed(2)))} disabled={loading} />
        <span className="t-num hidden min-w-[44px] text-center text-sm text-slate-300 sm:inline">{Math.round(zoom * 100)}%</span>
        <IconButton icon={ZoomIn} label="Inzoomen" onClick={() => setZoom((z) => Math.min(4, +(z + 0.2).toFixed(2)))} disabled={loading} />
        <IconButton icon={Maximize2} label="Passend maken" onClick={() => fitTo(pdfRef.current, page)} disabled={loading} />
        <div className="ml-auto flex">
          <IconButton icon={ExternalLink} label="Openen in nieuw tabblad" onClick={() => openInTab(src)} className="hidden sm:inline-flex" />
          <IconButton icon={Download} label="Downloaden" onClick={() => downloadFile(src, filename)} />
        </div>
      </div>

      <div ref={scrollRef} className="flex flex-1 items-start justify-center overflow-auto p-4">
        {loading && <Skeleton className="aspect-[1/1.414] w-full max-w-md" />}
        {error && !loading && (
          <div className="max-w-md pt-12 text-center">
            <p className="text-sm text-slate-200">Dit document kan hier niet worden getoond.</p>
            <p className="mt-1 break-words text-xs text-slate-500">{error}</p>
            <button type="button" onClick={() => openInTab(src)} className="btn-secondary mt-4">
              <ExternalLink className={ICON.sm} aria-hidden /> Openen in nieuw tabblad
            </button>
          </div>
        )}
        {!error && <canvas ref={canvasRef} className={cn('rounded-sm bg-white', loading && 'hidden')} />}
      </div>
    </div>
  );
}
